/**
 * ProductUpsell
 * Handles the upsell functionality for product pages
 */
class ProductUpsell {
  constructor(container) {
    this.container = container;
    this.upsellVariantId = container.querySelector('[data-upsell-variant-id]');
    this.mainVariantId = container.querySelector('[data-main-variant-id]');
    this.upsellProductId = container.querySelector('[data-upsell-product-id]').value;
    this.mainProductId = container.querySelector('[data-main-product-id]').value;
    this.addButton = container.querySelector('[data-upsell-add-button]');
    this.addButtonText = container.querySelector('[data-upsell-add-text]');
    this.addButtonPrice = container.querySelector('[data-upsell-add-price]');
    this.featuredImage = container.querySelector('[data-upsell-featured-image]');
    
    // Get all variants from the JSON
    const variantsJSON = document.querySelector(`[data-upsell-variants]`);
    this.variants = variantsJSON ? JSON.parse(variantsJSON.textContent) : [];
    
    // Get option selectors
    this.optionSelectors = Array.from(container.querySelectorAll('.product-upsell__option-selector'));
    
    // Initialize
    this.init();
  }

  /**
   * Initialize the upsell component
   */
  init() {
    // Set up event listeners for variant selectors
    this.setupEventListeners();
    
    // Initialize the first available variant
    this.selectInitialVariant();
    
    // Update the add button price
    this.updateAddButtonPrice();
    
    // Listen for changes to the main product form
    this.listenForMainProductChanges();
  }

  /**
   * Set up event listeners for variant selection
   */
  setupEventListeners() {
    // Color swatches
    this.container.querySelectorAll('.product-upsell__color-swatch').forEach(swatch => {
      swatch.addEventListener('click', (event) => {
        if (event.currentTarget.classList.contains('is-disabled')) return;
        
        const optionValue = event.currentTarget.getAttribute('data-option-value');
        const optionSelector = event.currentTarget.closest('.product-upsell__option-selector');
        const optionPosition = parseInt(optionSelector.getAttribute('data-option-position')) - 1;
        
        this.updateOptionSelection(optionSelector, optionValue);
        this.updateVariantSelection(optionPosition, optionValue);
      });
    });

    // Option buttons
    this.container.querySelectorAll('.product-upsell__option-button').forEach(button => {
      button.addEventListener('click', (event) => {
        if (event.currentTarget.disabled) return;
        
        const optionValue = event.currentTarget.getAttribute('data-option-value');
        const optionSelector = event.currentTarget.closest('.product-upsell__option-selector');
        const optionPosition = parseInt(optionSelector.getAttribute('data-option-position')) - 1;
        
        this.updateOptionSelection(optionSelector, optionValue);
        this.updateVariantSelection(optionPosition, optionValue);
      });
    });

    // Add to cart button
    this.addButton.addEventListener('click', () => this.addToCart());
  }

  /**
   * Updates the visual state of option selectors
   */
  updateOptionSelection(optionSelector, optionValue) {
    // Remove selected class from all options in this selector
    if (optionSelector.querySelector('.product-upsell__color-swatch')) {
      optionSelector.querySelectorAll('.product-upsell__color-swatch').forEach(el => {
        el.classList.remove('is-selected');
      });
      
      // Add selected class to the chosen option
      optionSelector.querySelector(`.product-upsell__color-swatch[data-option-value="${optionValue}"]`).classList.add('is-selected');
    } else {
      optionSelector.querySelectorAll('.product-upsell__option-button').forEach(el => {
        el.classList.remove('is-selected');
      });
      
      // Add selected class to the chosen option
      optionSelector.querySelector(`.product-upsell__option-button[data-option-value="${optionValue}"]`).classList.add('is-selected');
    }
  }

  /**
   * Updates the selected variant based on option selections
   */
  updateVariantSelection(optionPosition, optionValue) {
    // Get current selections for all options
    const currentSelections = this.getCurrentOptionSelections();
    currentSelections[optionPosition] = optionValue;
    
    // Find matching variant
    const matchingVariant = this.findVariantByOptions(currentSelections);
    
    if (matchingVariant) {
      // Update the variant ID input
      this.upsellVariantId.value = matchingVariant.id;
      
      // Update price
      this.updatePriceDisplay(matchingVariant);
      
      // Update image if available
      if (matchingVariant.featured_image) {
        this.updateFeaturedImage(matchingVariant.featured_image.src);
      }
      
      // Update add button price
      this.updateAddButtonPrice();
      
      // Update available options
      this.updateAvailableOptions(currentSelections);
    }
  }

  /**
   * Get current options selected for all option selectors
   */
  getCurrentOptionSelections() {
    return this.optionSelectors.map(selector => {
      const selected = selector.querySelector('.is-selected');
      return selected ? selected.getAttribute('data-option-value') : null;
    });
  }

  /**
   * Find a variant that matches the given options
   */
  findVariantByOptions(options) {
    return this.variants.find(variant => {
      return variant.options.every((option, index) => {
        return options[index] === option;
      });
    });
  }

  /**
   * Update price display with the selected variant's price
   */
  updatePriceDisplay(variant) {
    const priceContainer = this.container.querySelector('.product-upsell__price-container');
    
    if (variant.compare_at_price > variant.price) {
      priceContainer.innerHTML = `
        <span class="product-upsell__price product-upsell__price--sale">${this.formatMoney(variant.price)}</span>
        <span class="product-upsell__price product-upsell__price--compare">${this.formatMoney(variant.compare_at_price)}</span>
      `;
    } else {
      priceContainer.innerHTML = `
        <span class="product-upsell__price">${this.formatMoney(variant.price)}</span>
      `;
    }
  }

  /**
   * Update the featured image
   */
  updateFeaturedImage(src) {
    if (this.featuredImage && src) {
      // Extract base URL and add size parameter
      const imgUrl = this.getSizedImageUrl(src, '400x');
      this.featuredImage.src = imgUrl;
    }
  }

  /**
   * Update which options are available based on current selections
   */
  updateAvailableOptions(currentSelections) {
    // For each option position
    this.optionSelectors.forEach((selector, optionPosition) => {
      // Skip updating the option that was just changed
      if (currentSelections[optionPosition] === null) return;
      
      // For each value of the current option
      const optionElements = selector.querySelectorAll('.product-upsell__color-swatch, .product-upsell__option-button');
      
      optionElements.forEach(element => {
        const optionValue = element.getAttribute('data-option-value');
        
        // Make a copy of current selections and change just this option
        const testSelections = [...currentSelections];
        testSelections[optionPosition] = optionValue;
        
        // Check if any variant exists with these selections
        const variantExists = this.variants.some(variant => {
          let matches = true;
          testSelections.forEach((selection, i) => {
            // Skip null selections (not yet selected)
            if (selection === null) return;
            
            // Check if this option matches the variant
            if (variant.options[i] !== selection) {
              matches = false;
            }
          });
          
          return matches && variant.available;
        });
        
        // Update disabled state
        if (selector.querySelector('.product-upsell__color-swatch')) {
          element.classList.toggle('is-disabled', !variantExists);
        } else {
          element.classList.toggle('is-disabled', !variantExists);
          element.disabled = !variantExists;
        }
      });
    });
  }

  /**
   * Select the initial variant (first available)
   */
  selectInitialVariant() {
    // Start with the first variant
    const initialVariant = this.variants.find(variant => variant.available) || this.variants[0];
    
    if (initialVariant) {
      // Set each option to match this variant
      initialVariant.options.forEach((optionValue, index) => {
        const optionSelector = this.optionSelectors[index];
        if (optionSelector) {
          this.updateOptionSelection(optionSelector, optionValue);
        }
      });
      
      // Update variant ID and price
      this.upsellVariantId.value = initialVariant.id;
      this.updatePriceDisplay(initialVariant);
      
      // Update image if available
      if (initialVariant.featured_image) {
        this.updateFeaturedImage(initialVariant.featured_image.src);
      }
      
      // Update available options
      this.updateAvailableOptions(initialVariant.options);
    }
  }

  /**
   * Updates the combined price on the add button
   */
  updateAddButtonPrice() {
    // Get the current main product variant price
    const mainProductForm = document.getElementById(`product-form-${this.mainProductId}-${this.container.getAttribute('data-section-id')}`);
    
    if (!mainProductForm) return;
    
    // Get selected main variant
    const mainVariantId = mainProductForm.querySelector('input[name="id"]').value;
    this.mainVariantId.value = mainVariantId;
    
    // Find price of main product
    const mainPriceElement = document.querySelector('.product-info__price .price__regular .price__amount');
    const mainPrice = mainPriceElement ? this.extractPrice(mainPriceElement.textContent) : 0;
    
    // Find price of upsell product
    const upsellVariantId = this.upsellVariantId.value;
    const upsellVariant = this.variants.find(v => v.id.toString() === upsellVariantId.toString());
    const upsellPrice = upsellVariant ? upsellVariant.price : 0;
    
    // Calculate combined price
    const combinedPrice = mainPrice + upsellPrice;
    
    // Update button text
    this.addButtonPrice.textContent = `${(combinedPrice/100).toFixed(0)}`;
  }

  /**
   * Listen for changes to the main product form
   */
  listenForMainProductChanges() {
    const mainProductForm = document.getElementById(`product-form-${this.mainProductId}-${this.container.getAttribute('data-section-id')}`);
    
    if (mainProductForm) {
      // Listen for changes on the form
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            this.updateAddButtonPrice();
          }
        }
      });
      
      // Observe the variant ID input
      const variantInput = mainProductForm.querySelector('input[name="id"]');
      if (variantInput) {
        observer.observe(variantInput, { attributes: true });
      }
      
      // Also listen for form events
      mainProductForm.addEventListener('change', () => {
        this.updateAddButtonPrice();
      });
    }
  }

  /**
   * Add both products to cart
   */
  async addToCart() {
    // Show loading state
    this.addButton.setAttribute('aria-busy', 'true');
    
    try {
      // Get the variant IDs
      const mainVariantId = this.mainVariantId.value;
      const upsellVariantId = this.upsellVariantId.value;
      
      // Add both items to cart
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [
            {
              id: mainVariantId,
              quantity: 1
            },
            {
              id: upsellVariantId,
              quantity: 1
            }
          ]
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }
      
      // Get the response data
      const responseData = await response.json();
      
      // Refresh cart drawer if it exists
      const cartRefreshEvent = new CustomEvent('cart:refresh', { bubbles: true });
      document.documentElement.dispatchEvent(cartRefreshEvent);
      
      // Open cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.show();
      } else {
        // Fallback to redirecting to cart page
        window.location.href = '/cart';
      }
    } catch (error) {
      console.error('Error adding products to cart:', error);
      // Display error message
      alert('Er is een fout opgetreden. Probeer het opnieuw.');
    } finally {
      // Remove loading state
      this.addButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Extract price from text
   */
  extractPrice(text) {
    if (!text) return 0;
    
    // Remove currency symbols and non-numeric characters
    const numericString = text.replace(/[^0-9.,]/g, '').replace(',', '.');
    return parseFloat(numericString) || 0;
  }

  /**
   * Format money amount
   */
  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    const value = parseInt(cents || 0, 10);
    const money = new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(value / 100);
    
    return money;
  }

  /**
   * Get sized image URL
   */
  getSizedImageUrl(src, size) {
    if (!src) return '';
    
    // Check if this is already a resized URL
    if (src.includes('_' + size + '.')) {
      return src;
    }
    
    // Find the last dot in the URL (extension)
    const dotIndex = src.lastIndexOf('.');
    if (dotIndex === -1) return src;
    
    // Insert the size before the extension
    return src.substring(0, dotIndex) + '_' + size + src.substring(dotIndex);
  }
}

// Initialize all upsell components when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const upsellContainers = document.querySelectorAll('.product-upsell');
  upsellContainers.forEach(container => new ProductUpsell(container));
});